import { useMemo } from 'react';
import contentData from '@/data/website-content.json';
import { Footer, Homepage, Navigation, SiteMetadata, Social } from '@/types';

// Type definitions for better TypeScript support
export interface ContentData {
  siteMetadata: SiteMetadata;
  navigation: Navigation;
  homepage: Homepage;
  social: Social;
  footer: Footer;
  pages: {
    [key: string]: unknown;
  };
  ui: {
    buttons: { [key: string]: string };
    messages: { [key: string]: string };
    forms: { [key: string]: string };
  };
}

/**
 * Custom hook to access website content data
 * @returns {ContentData} The complete website content object
 */
export const useContent = (): ContentData => {
  return useMemo(() => contentData as unknown as ContentData, []);
};

/**
 * Helper function to safely access nested object properties
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.');
  let current: unknown = obj;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  
  return current;
}

/**
 * Hook to get content for a specific section
 * @param section - The section path (e.g., 'homepage.hero', 'navigation.items')
 * @returns The content for the specified section
 */
export const useContentSection = (section: string): unknown => {
  const content = useContent();
  
  return useMemo(() => {
    return getNestedValue(content as unknown as Record<string, unknown>, section);
  }, [content, section]);
};

/**
 * Hook to get page-specific content
 * @param pageName - The name of the page
 * @returns The content for the specified page
 */
export const usePageContent = (pageName: string): unknown => {
  const content = useContent();
  
  return useMemo(() => {
    return content.pages[pageName] || null;
  }, [content, pageName]);
};

/**
 * Hook to get UI text/messages
 * @param category - The UI category ('buttons', 'messages', 'forms')
 * @param key - The specific key within the category
 * @returns The UI text for the specified key
 */
export const useUIText = (category: 'buttons' | 'messages' | 'forms', key: string): string => {
  const content = useContent();
  
  return useMemo(() => {
    return content.ui[category][key] || key;
  }, [content, category, key]);
};

// Utility functions for commonly used content
export const useSiteMetadata = () => useContentSection('siteMetadata');
export const useNavigation = () => useContentSection('navigation');
export const useHomepage = () => useContentSection('homepage');
export const useSocialLinks = () => useContentSection('social.links');
export const useFooterData = () => useContentSection('footer'); 